/**
 * Contrast-correction for tenant accent colours used as text.
 *
 * A tenant pack's `accent` role is chosen to look right, not to be legible as
 * flat text on that tenant's own surface, and three of the four shipped packs
 * fail WCAG AA somewhere. Measured against the surface each pack declares,
 * WeBuild AI's orange is 2.83:1 in light mode, NGV's blue is 2.84:1 in dark and
 * NGET's is 3.52:1 in dark, while every LSEG combination clears 8:1. The
 * `styleguides/STYLE-GUIDE-LSEG.md` accessibility section requires 4.5:1 for
 * all text and says outright that the accent is "for graphical accents, not
 * body text", so the accent cannot be painted onto a heading unaltered.
 *
 * WCAG's large-text exemption is governed by font size and weight, being
 * 18.66px bold or 24px regular, so a short string does not qualify for the
 * lower 3:1 threshold merely by being short. Every accent-coloured string in
 * the chat surfaces is `text-sm` or smaller and therefore needs the full 4.5:1.
 *
 * The correction preserves the hue and only moves the colour towards black or
 * white, whichever direction the surface requires, by the smallest amount that
 * reaches the target ratio. An accent that already passes is returned byte for
 * byte unchanged, which is why LSEG renders exactly as its pack specifies and
 * only the failing tenants are touched.
 */

/** WCAG 2.1 AA for normal-sized text (1.4.3). */
export const MIN_TEXT_CONTRAST = 4.5;

type Rgb = readonly [number, number, number];

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Parses a three- or six-digit hex colour into 0-255 channels.
 *
 * Returns null rather than throwing, because the value ultimately comes from a
 * tenant manifest and a malformed one must degrade to the uncorrected colour
 * rather than take the chat surface down with it.
 */
const parseHex = (value: string): Rgb | null => {
  const match = HEX_PATTERN.exec(value.trim());

  if (!match) return null;

  const digits =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : match[1];

  return [
    parseInt(digits.slice(0, 2), 16),
    parseInt(digits.slice(2, 4), 16),
    parseInt(digits.slice(4, 6), 16),
  ];
};

const toHex = ([r, g, b]: Rgb): string =>
  `#${[r, g, b]
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;

/** Linearises one 0-255 sRGB channel, per the WCAG relative luminance formula. */
const linearise = (channel: number): number => {
  const proportion = channel / 255;

  return proportion <= 0.03928 ? proportion / 12.92 : Math.pow((proportion + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance, 0 for black through 1 for white. */
export const relativeLuminance = ([r, g, b]: Rgb): number =>
  0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);

/**
 * The WCAG relative luminance of a hex colour, or null if it cannot be parsed.
 *
 * Exported so there is one implementation of the sRGB linearisation in the
 * codebase. `getReadableTextColor` in `src/utils/color.ts` carried a second copy
 * of the identical formula, and two copies of the same maths can drift and then
 * disagree about the same brand colour. That function keeps its own non-WCAG
 * threshold, which is a separate decision from how luminance is computed.
 */
export const hexLuminance = (value: string): number | null => {
  const rgb = parseHex(value);

  return rgb ? relativeLuminance(rgb) : null;
};

/** WCAG contrast ratio between two luminances, 1:1 through 21:1. */
const ratioBetween = (a: number, b: number): number => {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * The WCAG contrast ratio between two hex colours, or null if either cannot be
 * parsed. Exported for the tests and for any caller that needs to report the
 * figure rather than act on it.
 */
export const contrastRatio = (foreground: string, background: string): number | null => {
  const fg = parseHex(foreground);
  const bg = parseHex(background);

  if (!fg || !bg) return null;

  return ratioBetween(relativeLuminance(fg), relativeLuminance(bg));
};

/**
 * Mixes towards the target and rounds to 8-bit channels in the same step.
 *
 * The rounding has to happen before the ratio is measured rather than after the
 * search settles. A fraction that clears 4.5:1 in continuous space can round
 * down to a colour that does not, which is how the first version of this
 * function emitted a WeBuild AI orange at 4.49:1 while believing it had cleared
 * the threshold.
 */
const mix = (colour: Rgb, target: Rgb, amount: number): Rgb => [
  Math.round(colour[0] + (target[0] - colour[0]) * amount),
  Math.round(colour[1] + (target[1] - colour[1]) * amount),
  Math.round(colour[2] + (target[2] - colour[2]) * amount),
];

const BLACK: Rgb = [0, 0, 0];
const WHITE: Rgb = [255, 255, 255];

/**
 * The surface luminance at which black and white are equally legible, being the
 * solution to `(L + 0.05) / 0.05 = 1.05 / (L + 0.05)`. Below it white wins and
 * above it black does, so it is the correct place to switch direction rather
 * than the mid-grey 0.5 that intuition suggests.
 */
const CROSSOVER_LUMINANCE = Math.sqrt(1.05 * 0.05) - 0.05;

/** Enough passes to settle the mix fraction well inside one 8-bit step. */
const SEARCH_ITERATIONS = 24;

/**
 * Returns the accent unchanged when it already meets `minRatio` against the
 * surface, and otherwise the nearest darker or lighter variant of it that does.
 *
 * The direction is chosen from the surface rather than from the accent, because
 * moving away from the surface is the only direction in which contrast rises
 * monotonically, which is what makes the binary search below valid. A light
 * surface therefore darkens the accent and a dark surface lightens it.
 *
 * An unparseable accent or surface yields the original accent, so a malformed
 * tenant manifest costs the correction rather than the colour.
 */
export const ensureAccessibleAccent = (
  accent: string,
  surface: string,
  minRatio: number = MIN_TEXT_CONTRAST,
): string => {
  const accentRgb = parseHex(accent);
  const surfaceRgb = parseHex(surface);

  if (!accentRgb || !surfaceRgb) return accent;

  const surfaceLuminance = relativeLuminance(surfaceRgb);

  if (ratioBetween(relativeLuminance(accentRgb), surfaceLuminance) >= minRatio) {
    return accent;
  }

  // Move away from the surface, towards whichever extreme the surface is further
  // from. `CROSSOVER_LUMINANCE` is the luminance at which black and white give
  // identical contrast, so this always picks the direction with more headroom.
  const target = surfaceLuminance > CROSSOVER_LUMINANCE ? BLACK : WHITE;

  // The extreme is the best available, so where even that falls short there is
  // no colour that would do better. At the 4.5:1 default this cannot happen,
  // because the better extreme always clears 4.5:1 from any surface; it becomes
  // reachable once a caller asks for a higher ratio such as AAA.
  if (ratioBetween(relativeLuminance(target), surfaceLuminance) < minRatio) {
    return toHex(target);
  }

  // Contrast rises monotonically with the mix fraction, so bisect for the
  // smallest adjustment that clears the target and keep as much of the
  // tenant's own colour as the ratio permits.
  let low = 0;
  let high = 1;

  for (let pass = 0; pass < SEARCH_ITERATIONS; pass += 1) {
    const mid = (low + high) / 2;

    if (
      ratioBetween(relativeLuminance(mix(accentRgb, target, mid)), surfaceLuminance) >= minRatio
    ) {
      high = mid;
    } else {
      low = mid;
    }
  }

  return toHex(mix(accentRgb, target, high));
};
